import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import AddUserModal from "../modals/AddUserModal";
import SetUserPinModal from "../modals/SetUserPinModal";
import AssignAccessRolesModal from "../modals/AssignAccessRolesModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ButtonDelete, ButtonEdit, ButtonSetPin } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useDismissEmployeeMutation,
  useGetUsersQuery,
} from "@/redux/services/user/user.api";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Roles, User } from "@/redux/services/user/user.type";
import {
  formatDate,
  formatRoles,
  getInitials,
  getProfilePictureUrl,
} from "@/lib/utils";
import { useModal, usePagination } from "@/lib/hooks";
import DataTable, { Column } from "@/components/ui/DataTable";
import { useAppSelector } from "@/redux/features/hooks";
import { useCanAdministerRoles } from "@/hooks/useCanAdministerRoles";

const ListeUtilisateurs = () => {
  const { limit, page, onChange } = usePagination(10);
  const {
    currentUser,
    currentRestaurant: restaurant,
    currentFranchise,
  } = useAppSelector((s) => s.user);
  const canAdministerRoles = useCanAdministerRoles();
  // User edit and PIN reset stay manager/owner-role gated (not delegatable). An
  // employees.view delegate sees the list read-only.
  const canManageUsers =
    (currentUser?.roles?.includes(Roles.manager) ||
      currentUser?.roles?.includes(Roles.owner)) ??
    false;
  // Dismissing an employee is reserved to the primary manager and the owner
  // (the API refuses it for secondary managers).
  const canDismiss =
    (currentUser?.roles?.includes(Roles.owner) ||
      currentUser?.isPrimaryManager) ??
    false;

  const { data, isLoading } = useGetUsersQuery({
    limit,
    page,
    restaurantId: restaurant?.id as string,
    franchiseId: currentFranchise?.id,
  });
  const { isOpen, item, closeModal, openEditModal } = useModal<User>();
  const [pinTarget, setPinTarget] = useState<User | null>(null);
  const [rolesTarget, setRolesTarget] = useState<User | null>(null);
  const [dismissEmployee] = useDismissEmployeeMutation();
  const confirm = useConfirm();

  const handleDismiss = async (user: User) => {
    if (!user.employeeId) return;
    const ok = await confirm({
      title: `Retirer ${user.fullName} de l'équipe ?`,
      description:
        "Cette personne n'aura plus accès au restaurant. Son compte et son historique sont conservés.",
      confirmText: "Retirer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await dismissEmployee(user.employeeId).unwrap();
      toast.success("Employé retiré de l'équipe.");
    } catch {
      toast.error("Impossible de retirer cet employé.");
    }
  };

  const columns: Column[] = [
    {
      key: "fullName",
      label: "Nom complet",
      formatter: (_value: any, row: any) => {
        const user = row as User;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={getProfilePictureUrl(user?.image?.url)}
                alt="avatar"
              />
              <AvatarFallback className="text-xs">
                {getInitials(user?.fullName)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{user?.fullName}</span>
          </div>
        );
      },
    },
    { key: "email", label: "Email" },
    { key: "phone", label: "Téléphone" },
    {
      key: "roles",
      label: "Rôle",
      formatter: (_value: any, row: any) => {
        const user = row as User;
        const roleColors: Record<string, string> = {
          [Roles.manager]: "bg-purple-100 text-purple-700",
          [Roles.waiter]: "bg-blue-100 text-blue-700",
          [Roles.kitchen]: "bg-orange-100 text-orange-700",
          [Roles.cashier]: "bg-accent-light text-accent",
          [Roles.owner]: "bg-primary-light text-primary",
          [Roles.admin]: "bg-red-100 text-red-700",
        };
        const role = user?.roles?.find((r) => r !== Roles.user);
        const colorClass = role ? (roleColors[role] ?? "bg-muted text-muted-foreground") : "bg-muted text-muted-foreground";
        return (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${colorClass}`}>
            {formatRoles(user?.roles)}
          </span>
        );
      },
    },
    {
      key: "lastLoginAt",
      label: "Dernière connexion",
      nowrap: true,
      formatter: (_value: any, row: any) => {
        const user = row as User;
        return user.lastLoginAt
          ? formatDate(new Date(user.lastLoginAt), true, true)
          : "Jamais";
      },
    },
    {
      key: "actions",
      label: "Actions",
      formatter: (_value: any, row: any) => {
        const user = row as User;
        return (
          <div className="flex items-center gap-2">
            {canAdministerRoles && (
              <button
                type="button"
                title="Rôles d'accès"
                onClick={() => setRolesTarget(user)}
                className={cn(
                  "h-7 w-7 rounded-full flex items-center justify-center transition-colors",
                  "bg-primary-light hover:bg-primary-light/70"
                )}
              >
                <ShieldCheck className="h-4 w-4 text-primary" />
              </button>
            )}
            {canManageUsers && (
              <>
                <ButtonSetPin onClick={() => setPinTarget(user)} />
                <ButtonEdit onClick={() => openEditModal(user)} />
              </>
            )}
            {canDismiss && user.employeeId && (
              <ButtonDelete onClick={() => handleDismiss(user)} />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="h-full">
      <DataTable
        data={data?.results ?? []}
        columns={columns}
        loading={isLoading}
        totalCount={data?.count ?? 0}
        currentPage={page}
        pageSize={limit}
        onPageChange={(p) => onChange(limit, p)}
        onPageSizeChange={(size) => onChange(size, 1)}
        searchable={false}
        emptyMessage="Aucun utilisateur trouvé"
      />
      {isOpen && (
        <AddUserModal item={item} isOpen={isOpen} onClose={closeModal} />
      )}
      {pinTarget && (
        <SetUserPinModal
          item={pinTarget}
          isOpen={true}
          onClose={() => setPinTarget(null)}
        />
      )}
      {rolesTarget && (
        <AssignAccessRolesModal
          user={rolesTarget}
          isOpen={true}
          onClose={() => setRolesTarget(null)}
        />
      )}
    </div>
  );
};

export default ListeUtilisateurs;
